import {
	BookOutlined,
	DollarOutlined,
	ExclamationCircleOutlined,
	FilterOutlined,
	LeftOutlined,
	RightOutlined,
	UploadOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { Button, Popover, Select, Table, Tooltip, Upload } from 'antd';
import DownloadComponent from 'components/Global/Download';
import InputTimeLogsHoursField from 'components/InputTimeLogsHoursField';
import { useEffect, useState } from 'react';
import { FaFileImage, FaFilePdf } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { formatDate, invalidText, timeStringToMinutes } from 'utils/utils';

const { Column } = Table;

const LogAssignmentTable = ({
	tableData,
	totalRecords,
	currentPage,
	pageSize,
	isLoading = false,
	tableChangeHandler = () => {},
	setTableData,
	shiftOptions = [], // Add shift options prop
	handleLogChange,
	onSave,
	isSunday,
	isHoliday,
	realCurrentRecords,
	uploadedFile,
	fileDocUrl,
	uploadProps,
	handlePageSizeChange,
	setIsDataChanged,
	isSubmitLoading,
	filterPopoverVisible,
	setFilterPopoverVisible,
	setSelectedApplyAllShift,
	handleApplyShiftToAll,
	selectedApplyAllShift,
	isSundayRegularHours,
}: // Add save handler prop
// Add this prop to use your existing function
any) => {
	const handleInputChange = (
		employeeId: string,
		field: string,
		value: any
	) => {
		// Use your existing handleLogChange function instead
		handleLogChange(employeeId, field, value);
	};
	const { data } = useSelector((state: any) => state?.userProfile);

	// Format shift options for Select component - they're already formatted from API
	const formatShiftOptionsForSelect = () => {
		return shiftOptions;
	};

	// Calculate total pages
	// const totalPages = Math.ceil(totalRecords / pageSize);
	const [totalPages, setTotalPages] = useState<number>(1);

	// Custom pagination handlers
	const handlePrevious = () => {
		if (currentPage > 1) {
			tableChangeHandler({ current: currentPage - 1, pageSize });
		}
	};

	const handleSave = () => {
		if (onSave) {
			onSave();
		}
	};

	// Handle save and next action
	const handleSaveAndNext = () => {
		if (onSave) {
			onSave();
		}
		// After saving, move to next page if not on last page
		if (currentPage < totalPages) {
			setIsDataChanged(true);
			tableChangeHandler({ current: currentPage + 1, pageSize });
		}
	};

	// Check if employee is absent based on hours
	const isEmployeeAbsent = (record: any) => {
		if (isHoliday) {
			return false;
		}
		const regularMinutes = timeStringToMinutes(
			record.regularHours || '00:00'
		);
		const overtimeMinutes = timeStringToMinutes(
			record.overtimeHours || '00:00'
		);
		return regularMinutes === 0 && overtimeMinutes === 0;
	};

	// Calculate and set total pages when dependencies change
	useEffect(() => {
		if (totalRecords && pageSize) {
			const calculatedTotalPages = Math.ceil(totalRecords / pageSize);
			setTotalPages(calculatedTotalPages);
		}
	}, [totalRecords, pageSize]);

	// Calculate eligible employees for apply to all
	const eligibleEmployees = tableData.filter(
		(item: any) => !item.isTotal && !item.isPayrollRun
	).length;

	// Filter popover content
	const filterPopoverContent = () => (
		<div style={{ width: '280px', padding: '8px' }}>
			<div style={{ marginBottom: '12px' }}>
				<p
					style={{
						display: 'block',
						marginBottom: '6px',
						fontSize: '14px',
						fontWeight: 500,
						color: '#333',
					}}
				>
					Select Shift Type:
				</p>
				<Select
					value={selectedApplyAllShift}
					onChange={setSelectedApplyAllShift}
					options={formatShiftOptionsForSelect()}
					style={{ width: '100%' }}
					size="small"
					placeholder="Select shift type to apply"
					disabled={
						isLoading || isSubmitLoading || eligibleEmployees === 0
					}
				/>
			</div>

			<div
				style={{
					display: 'flex',
					justifyContent: 'end',
					alignItems: 'center',
					marginTop: '15px',
				}}
			>
				<Button
					type="primary"
					size="small"
					onClick={handleApplyShiftToAll}
					disabled={
						!selectedApplyAllShift ||
						isLoading ||
						isSubmitLoading ||
						eligibleEmployees === 0
					}
					style={{
						fontSize: '13px',
					}}
				>
					{selectedApplyAllShift
						? `Apply to All (${eligibleEmployees})`
						: 'Apply to All (0)'}
				</Button>
			</div>
		</div>
	);

	// Custom pagination component
	const customPagination = () => {
		const isLastPage = currentPage >= totalPages;
		let btnText = '';
		if (isSubmitLoading) btnText = 'Saving...';
		else if (isLoading || isLastPage) btnText = 'Save';
		else btnText = 'Save & Next';

		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '12px 20px',
					backgroundColor: '#fff',
					borderRadius: '8px',
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'end',
						alignItems: 'center',
					}}
				>
					<div
						style={{
							marginRight: '10px',
						}}
					>
						<Upload {...uploadProps} showUploadList={false}>
							<Tooltip
								title="Allow uploading manual time logs in PDF or image format (Max: 5MB)."
								placement="top"
							>
								<Button
									icon={<UploadOutlined />}
									disabled={isLoading || isSubmitLoading}
								>
									Add Attachment
								</Button>
							</Tooltip>
						</Upload>
					</div>
					{uploadedFile?.[0]?.name &&
						(() => {
							const fileNameOrUrl = invalidText(fileDocUrl)
								? uploadedFile[0].name
								: fileDocUrl;
							return (
								<DownloadComponent
									documentUrl={
										invalidText(fileDocUrl)
											? uploadedFile[0]
											: fileDocUrl
									}
									icon={
										/\.(pdf)$/i.test(fileNameOrUrl) ? (
											<FaFilePdf
												color="#d32f2f"
												size={20}
											/>
										) : (
											<FaFileImage
												color="#1976d2"
												size={20}
											/>
										)
									}
									showTooltip={true}
									downloadText={
										uploadedFile[0].name.split('.')[0]
									}
									tooltipPlacement="top"
									tooltipText={uploadedFile[0]?.name}
									isLocalFile={invalidText(fileDocUrl)}
									localFile={
										invalidText(fileDocUrl)
											? uploadedFile[0]
											: null
									}
								/>
							);
						})()}
				</div>
				{/* Compact Info */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					{totalPages !== null && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								padding: '6px 12px',
								background: '#584495',
								color: 'white',
								borderRadius: '20px',
								fontSize: '13px',
								fontWeight: 600,
							}}
						>
							<BookOutlined style={{ fontSize: '14px' }} />
							Page {currentPage}/{totalPages}
						</div>
					)}

					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							fontSize: '14px',
							color: '#555',
						}}
					>
						<UserOutlined
							style={{ fontSize: '16px', color: '#666' }}
						/>
						<span style={{ fontSize: '16px', color: '#584495' }}>
							<b>{realCurrentRecords.toLocaleString()} </b>
						</span>
						{''}employees
					</div>
				</div>
				{/* Compact Controls */}
				<div style={{ display: 'flex', gap: '6px' }}>
					{/* Page Size Selector */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
						}}
					>
						<span
							style={{
								fontSize: '14px',
								color: '#666',
								fontWeight: 500,
							}}
						>
							Show :
						</span>
						<select
							value={pageSize}
							onChange={(e: any) =>
								handlePageSizeChange(parseInt(e.target.value))
							}
							disabled={isLoading}
							style={{
								padding: '8px 14px',
								border: '1px solid #ccc',
								backgroundColor: '#fdfcff',
								color: '#584495',
								borderRadius: '8px',
								fontSize: '14px',
								fontWeight: 600,
								cursor: isLoading ? 'not-allowed' : 'pointer',
								outline: 'none',
								minWidth: '70px',
								transition: 'all 0.2s ease-in-out',
								boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
							}}
							onMouseEnter={(e: any) => {
								if (!isLoading) {
									e.target.style.backgroundColor = '#f0ecfa';
									e.target.style.borderColor = '#584495';
								}
							}}
							onMouseLeave={(e: any) => {
								if (!isLoading) {
									e.target.style.backgroundColor = '#fdfcff';
									e.target.style.borderColor = '#ccc';
								}
							}}
							onFocus={(e: any) => {
								e.target.style.borderColor = '#7b5eea';
								e.target.style.boxShadow =
									'0 0 0 2px rgba(123, 94, 234, 0.2)';
							}}
							onBlur={(e) => {
								e.target.style.borderColor = '#ccc';
								e.target.style.boxShadow = 'none';
							}}
						>
							<option value={51}>50</option>{' '}
							<option value={101}>100</option>
							<option value={151}>150</option>
							<option value={201}>200</option>
							<option value={501}>500</option>
						</select>
					</div>

					{currentPage > 1 && (
						<button
							onClick={handlePrevious}
							style={{
								padding: '8px 12px',
								border: '1px solid #ccc',
								background: 'white',
								color: '#666',
								borderRadius: '6px',
								fontSize: '13px',
								fontWeight: 500,
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: '4px',
								transition: 'background 0.2s',
							}}
							onMouseEnter={(e: any) =>
								(e.target.style.backgroundColor = '#f6f4fc')
							}
							onMouseLeave={(e: any) =>
								(e.target.style.backgroundColor = 'white')
							}
						>
							<LeftOutlined style={{ fontSize: '14px' }} /> Prev
						</button>
					)}

					{totalPages !== null && (
						<button
							onClick={
								isLastPage ? handleSave : handleSaveAndNext
							}
							disabled={isLoading || isSubmitLoading}
							style={{
								background: '#584495',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								padding: '8px 16px',
								fontSize: '13px',
								fontWeight: 600,
								cursor:
									isLoading || isSubmitLoading
										? 'not-allowed'
										: 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: '4px',
								opacity:
									isLoading || isSubmitLoading ? 0.6 : 1,
								transition: 'background 0.3s',
							}}
							onMouseEnter={(e: any) => {
								if (!isLoading && !isSubmitLoading)
									e.target.style.backgroundColor = '#453363';
							}}
							onMouseLeave={(e: any) => {
								if (!isLoading && !isSubmitLoading)
									e.target.style.backgroundColor = '#584495';
							}}
						>
							{btnText}
							{!isLoading && !isSubmitLoading && !isLastPage && (
								<RightOutlined style={{ fontSize: '14px' }} />
							)}
						</button>
					)}
				</div>
			</div>
		);
	};

	return (
		<>
			<Table
				dataSource={tableData}
				rowKey="employeeId"
				scroll={{ y: 'calc(100vh - 264px)' }}
				loading={isLoading}
				pagination={false} // Disable default pagination
				onChange={tableChangeHandler}
				rowClassName={(record: any, index) => {
					// Base class for sticky row
					const stickyClass =
						index === tableData.length - 1 ? 'sticky-row' : '';

					// Highlight class if canEdit is false
					const highlightClass =
						record.originalSupervisorInfo &&
						!record.originalSupervisorInfo.canEdit
							? 'non-editable-row'
							: '';

					// Combine classes
					return `${stickyClass} ${highlightClass}`.trim();
				}}
				className="logAssignmentTable"
			>
				<Column
					title="Employee Name | Code"
					dataIndex="employeeName"
					key="employeeName"
					width="28%"
					className="bg-white"
					render={(text: string, record: any) => {
						if (record.isTotal)
							return (
								<span className="color-purple font-bold">
									Total
								</span>
							);
						const isAbsent = isEmployeeAbsent(record);
						let title = '';
						if (record.leaveDetails.isPaidLeave)
							title = 'On Full Day Paid leave';
						else if (record.leaveDetails.isLwp)
							title = 'On Full Day LWP leave';
						else if (
							isAbsent &&
							(!isSunday || isSundayRegularHours)
						)
							title = 'Marked as Absent';
						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									whiteSpace: 'nowrap',
								}}
							>
								<Tooltip title={title}>
									<span
										style={{
											color:
												record.leaveDetails.isLwp ||
												record.leaveDetails
													.isPaidLeave ||
												(isAbsent &&
													(!isSunday ||
														isSundayRegularHours))
													? 'red'
													: 'inherit',
											display: 'flex',
											alignItems: 'center',
											gap: '0.5rem',
										}}
									>
										{record.isPayrollRun &&
											record?.payrollStatus && (
												<Tooltip
													title={
														record.payrollStatus ===
															'APPROVED' ||
														record.payrollStatus ===
															'FINALIZE'
															? 'Payroll is Approved/Finalized'
															: record.payrollStatus ===
															  'DRAFT'
															? 'Payroll is in Draft'
															: 'Payroll is Pending'
													}
												>
													<DollarOutlined
														style={{
															color:
																record.payrollStatus ===
																	'APPROVED' ||
																record.payrollStatus ===
																	'FINALIZE'
																	? 'green'
																	: record.payrollStatus ===
																	  'DRAFT'
																	? 'orange'
																	: 'grey',
														}}
													/>
												</Tooltip>
											)}
										{isAbsent && !isSunday && (
											<ExclamationCircleOutlined
												style={{
													color: 'red',
													fontSize: '14px',
												}}
											/>
										)}
										{record.employeeName} |{' '}
										<span style={{ color: '#584495' }}>
											{record.employeeCode}
										</span>
									</span>
								</Tooltip>
							</div>
						);
					}}
				/>

				<Column
					title={
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<b>Shift Type</b>
							<Popover
								content={filterPopoverContent}
								title="Apply Shift to All Employees"
								trigger="click"
								open={filterPopoverVisible}
								onOpenChange={(visible) => {
									setFilterPopoverVisible(visible);
									// Reset selection when popover is closed
									if (!visible) {
										setSelectedApplyAllShift('');
									}
								}}
								placement="bottomLeft"
								overlayStyle={{ zIndex: 1050 }}
							>
								<FilterOutlined
									style={{
										fontSize: '16px',
										color:
											eligibleEmployees > 0
												? '#584495'
												: '#ccc',
										cursor:
											eligibleEmployees > 0
												? 'pointer'
												: 'not-allowed',
										transition: 'color 0.2s',
									}}
									onClick={() => {
										if (eligibleEmployees > 0) {
											setFilterPopoverVisible(
												!filterPopoverVisible
											);
										}
									}}
								/>
							</Popover>
						</div>
					}
					dataIndex="shiftType"
					key="shiftType"
					width="20%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (record.isTotal) return <span></span>;
						let canEdit = true;
						if (record.originalSupervisorInfo) {
							const { canEdit: edit } =
								record.originalSupervisorInfo;
							canEdit = edit;
						}
						const currentShiftType =
							record.shiftDetails?.shiftType || value;

						return (
							<Select
								value={currentShiftType}
								disabled={record.isPayrollRun || !canEdit}
								onChange={(newValue) => {
									const selectedShift = shiftOptions.find(
										(opt: any) => opt.value === newValue
									);
									const updatedShiftDetails = {
										shiftType: newValue,
										shiftName:
											selectedShift?.shiftName ||
											newValue,
										shiftId: selectedShift?.id,
									};
									const newData = [...tableData];
									const index = newData.findIndex(
										(item) =>
											item.employeeId ===
											record.employeeId
									);
									if (index > -1) {
										newData[index] = {
											...newData[index],
											shiftDetails: updatedShiftDetails,
										};
										setTableData(newData);
										handleInputChange(
											record.employeeId,
											'shiftDetails',
											updatedShiftDetails
										);
									}
								}}
								options={formatShiftOptionsForSelect()}
								style={{
									fontSize: '14px',
									width: '195px',
								}}
								size="middle"
								popupMatchSelectWidth={false}
								placeholder="Select shift"
							/>
						);
					}}
				/>

				<Column
					title="Worked Hours"
					dataIndex="totalHours"
					key="totalHours"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						let canEdit = true;
						if (record.originalSupervisorInfo) {
							const { canEdit: edit } =
								record.originalSupervisorInfo;
							canEdit = edit;
						}
						if (record.isTotal) {
							return (
								<p
									className="color-purple font-bold"
									style={{
										textAlign: 'right',
										width: '30%',
									}}
								>
									{record.totalHours}
								</p>
							);
						}
						return (
							<InputTimeLogsHoursField
								value={record.totalHours}
								onChange={(newValue) =>
									handleInputChange(
										record.employeeId,
										'totalHours',
										newValue
									)
								}
								label=""
								required={false}
								name="totalHours"
								isError={false}
								disabled={
									record.leaveDetails.isLwp ||
									record.leaveDetails.isPaidLeave ||
									record.isPayrollRun ||
									!canEdit
								}
								showLabel={false}
								size="middle"
								style={{ width: '30%', textAlign: 'right' }}
								is8hoursRequired={isHoliday && !isSunday}
								isForeman={record?.isForeman}
							/>
						);
					}}
				/>

				<Column
					title="Shift Difference Hours"
					dataIndex="timeShiftHours"
					key="timeShiftHours"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (record.isTotal) {
							return (
								<span className="color-purple font-bold">
									{record.timeShiftHours}
								</span>
							);
						}

						const currentShiftType = record.shiftDetails?.shiftType;
						const displayValue =
							isHoliday && currentShiftType === 'NIGHT'
								? record.overtimeHours
								: currentShiftType === 'NIGHT'
								? record.timeShiftHours
								: '-';

						return (
							<span className="color-purple">{displayValue}</span>
						);
					}}
				/>

				<Column
					title="Original Supervisor"
					dataIndex="originalSupervisor"
					key="originalSupervisor"
					sorter={true}
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (
							record.isTotal ||
							invalidText(record.originalSupervisorInfo)
						) {
							return <p>-</p>;
						}
						const { supervisorName, modifiedBy, modifiedAt } =
							record.originalSupervisorInfo;
						return (
							<div>
								<p>Original Supervisor : {supervisorName}</p>
								{modifiedBy && (
									<p>Modified By : {modifiedBy}</p>
								)}
								{modifiedAt && (
									<p>
										{formatDate(
											modifiedAt,
											data?.companyDateFormat ??
												'DD/MM/YYYY'
										)}
									</p>
								)}
							</div>
						);
					}}
				/>
			</Table>

			{/* Custom pagination */}
			{tableData.length > 0 && customPagination()}
		</>
	);
};

export default LogAssignmentTable;
